import { useEffect, useRef } from 'react';

/**
 * Two-phase font-size fitting hook:
 *
 * Phase 1 (measurement): Starts at `startFontSize` and decrements until
 * `el.scrollWidth <= maxPx` (or the 16px floor is hit). Reports the final
 * size via `onSizeComputed` so a parent can collect sizes across multiple
 * instances and pick the global minimum.
 *
 * Phase 2 (forced): When `forcedFontSize` is provided (non-null, > 0), the
 * hook skips measurement entirely and just applies that size. This is how
 * the parent's shared minimum is pushed back down without triggering another
 * measurement → callback → state-update cycle.
 *
 * @param text           — reactive dep; re-runs whenever the name changes
 * @param maxPx          — pixel width budget (measured container width)
 * @param startFontSize  — largest candidate size in px (fill from top)
 * @param onSizeComputed — called once per measurement with the final px size
 * @param forcedFontSize — when set, skip measurement and apply this size
 */
export function useFitText(
  text: string,
  maxPx: number,
  startFontSize: number,
  onSizeComputed?: (size: number) => void,
  forcedFontSize?: number,
) {
  const ref = useRef<HTMLSpanElement>(null);
  // Keep callback in a ref so changing it doesn't re-trigger the effect
  const callbackRef = useRef(onSizeComputed);
  callbackRef.current = onSizeComputed;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // ── Phase 2: forced size ──────────────────────────────────────────────
    // Parent has computed the global minimum — just apply it and stop.
    // Do NOT call onSizeComputed here to avoid a feedback loop.
    if (forcedFontSize != null && forcedFontSize > 0) {
      el.style.fontSize = `${forcedFontSize}px`;
      return;
    }

    // ── Phase 1: measurement ──────────────────────────────────────────────
    if (maxPx <= 0 || startFontSize <= 0) return;

    // Start as large as possible, shrink until it fits the width budget
    el.style.fontSize = `${startFontSize}px`;
    let size = startFontSize;

    while (el.scrollWidth > maxPx && size > 16) {
      size -= 1;
      el.style.fontSize = `${size}px`;
    }

    callbackRef.current?.(size);
  }, [text, maxPx, startFontSize, forcedFontSize]);

  return ref;
}
